import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
} from 'recharts';

/**
 * LaunchTrajectoryChart - Combined chart showing launch trajectory
 *
 * Pattern from previous version:
 * - Bars for volume (orange)
 * - Lines for funnel metrics (awareness, trial, repeat)
 * - Dual Y-axis: percentage (left), volume (right)
 * - Week/period labels on X-axis
 */

interface TrajectoryDataPoint {
  period: string; // e.g., "S1", "S2", "M1", "M2"
  volume: number;
  awareness: number; // 0-100
  trial: number; // 0-100
  repeat: number; // 0-100
  distribution?: number; // 0-100
}

interface LaunchTrajectoryChartProps {
  data: TrajectoryDataPoint[];
  title?: string;
  height?: number;
  showLegend?: boolean;
  showGrid?: boolean;
  showConfidenceBands?: boolean;
  volumeUnit?: string; // e.g., "K", "M"
  language?: 'en' | 'es';
  className?: string;
}

// Chart colors matching design system
const COLORS = {
  volume: '#f97316', // Orange for volume bars
  awareness: '#14b8a6', // Teal
  trial: '#a855f7', // Purple
  repeat: '#ec4899', // Pink
  distribution: '#3b82f6', // Blue
};

export function LaunchTrajectoryChart({
  data,
  title,
  height = 300,
  showLegend = true,
  showGrid = true,
  showConfidenceBands = false,
  volumeUnit = 'K',
  language = 'en',
  className = '',
}: LaunchTrajectoryChartProps) {
  const t = language === 'es';

  // Calculate max volume for Y-axis domain
  const maxVolume = Math.max(...data.map((d) => d.volume));
  const volumeDomain = [0, Math.ceil(maxVolume * 1.1)];

  // Format volume for display
  const formatVolume = (value: number) => {
    if (volumeUnit === 'M') {
      return `${(value / 1000).toFixed(1)}M`;
    }
    return `${(value / 1000).toFixed(1)}K`;
  };

  return (
    <div className={`${className}`}>
      {title && (
        <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
      )}

      <div style={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={data}
            margin={{ top: 20, right: 60, bottom: 20, left: 20 }}
          >
            {showGrid && (
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            )}

            {/* X-Axis: Period labels */}
            <XAxis
              dataKey="period"
              tick={{ fill: '#94a3b8', fontSize: 12 }}
              axisLine={{ stroke: '#475569' }}
              tickLine={{ stroke: '#475569' }}
            />

            {/* Left Y-Axis: Percentage (0-100%) */}
            <YAxis
              yAxisId="left"
              domain={[0, 100]}
              tick={{ fill: '#94a3b8', fontSize: 12 }}
              axisLine={{ stroke: '#475569' }}
              tickLine={{ stroke: '#475569' }}
              tickFormatter={(value) => `${value}%`}
            />

            {/* Right Y-Axis: Volume */}
            <YAxis
              yAxisId="right"
              orientation="right"
              domain={volumeDomain}
              tick={{ fill: '#94a3b8', fontSize: 12 }}
              axisLine={{ stroke: '#475569' }}
              tickLine={{ stroke: '#475569' }}
              tickFormatter={formatVolume}
            />

            <Tooltip content={<TrajectoryTooltip language={language} volumeUnit={volumeUnit} />} />

            {showLegend && (
              <Legend
                wrapperStyle={{ paddingTop: 10 }}
                formatter={(value) => (
                  <span className="text-text-secondary text-sm">{value}</span>
                )}
              />
            )}

            {/* Volume Bars */}
            <Bar
              yAxisId="right"
              dataKey="volume"
              name={t ? 'Volumen' : 'Volume'}
              fill={COLORS.volume}
              radius={[4, 4, 0, 0]}
              opacity={0.8}
            />

            {/* Awareness Line */}
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="awareness"
              name={t ? 'Awareness' : 'Awareness'}
              stroke={COLORS.awareness}
              strokeWidth={2}
              dot={{ fill: COLORS.awareness, strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, strokeWidth: 0 }}
            />

            {/* Trial Line */}
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="trial"
              name={t ? 'Trial' : 'Trial'}
              stroke={COLORS.trial}
              strokeWidth={2}
              dot={{ fill: COLORS.trial, strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, strokeWidth: 0 }}
            />

            {/* Repeat Line */}
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="repeat"
              name={t ? 'Repeat' : 'Repeat'}
              stroke={COLORS.repeat}
              strokeWidth={2}
              dot={{ fill: COLORS.repeat, strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, strokeWidth: 0 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Custom Legend (optional, more detailed) */}
      {!showLegend && (
        <div className="flex flex-wrap justify-center gap-4 mt-4">
          <LegendItem color={COLORS.volume} label={t ? 'Volumen' : 'Volume'} shape="square" />
          <LegendItem color={COLORS.awareness} label="Awareness" />
          <LegendItem color={COLORS.trial} label="Trial" />
          <LegendItem color={COLORS.repeat} label="Repeat" />
        </div>
      )}
    </div>
  );
}

/**
 * Custom tooltip for trajectory chart
 */
function TrajectoryTooltip({
  active,
  payload,
  label,
  language = 'en',
  volumeUnit = 'K',
}: any) {
  if (!active || !payload || !payload.length) return null;

  const t = language === 'es';

  // Extract values from payload
  const data = payload[0]?.payload;

  return (
    <div className="bg-surface-800 border border-surface-400/30 rounded-lg px-4 py-3 shadow-xl">
      <p className="text-sm font-medium text-white mb-2">
        {t ? 'Período' : 'Period'}: {label}
      </p>

      <div className="space-y-1">
        {data?.volume !== undefined && (
          <div className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded"
              style={{ backgroundColor: COLORS.volume }}
            />
            <span className="text-sm text-text-secondary">
              {t ? 'Volumen' : 'Volume'}:{' '}
              <span className="text-white font-medium">
                {volumeUnit === 'M'
                  ? `${(data.volume / 1000).toFixed(1)}M`
                  : `${(data.volume / 1000).toFixed(1)}K`}
              </span>
            </span>
          </div>
        )}

        {data?.awareness !== undefined && (
          <div className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: COLORS.awareness }}
            />
            <span className="text-sm text-text-secondary">
              Awareness:{' '}
              <span className="text-white font-medium">{data.awareness.toFixed(1)}%</span>
            </span>
          </div>
        )}

        {data?.trial !== undefined && (
          <div className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: COLORS.trial }}
            />
            <span className="text-sm text-text-secondary">
              Trial:{' '}
              <span className="text-white font-medium">{data.trial.toFixed(1)}%</span>
            </span>
          </div>
        )}

        {data?.repeat !== undefined && (
          <div className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: COLORS.repeat }}
            />
            <span className="text-sm text-text-secondary">
              Repeat:{' '}
              <span className="text-white font-medium">{data.repeat.toFixed(1)}%</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Legend item component
 */
function LegendItem({
  color,
  label,
  shape = 'circle',
}: {
  color: string;
  label: string;
  shape?: 'circle' | 'square';
}) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={`w-3 h-3 ${shape === 'circle' ? 'rounded-full' : 'rounded'}`}
        style={{ backgroundColor: color }}
      />
      <span className="text-sm text-text-secondary">{label}</span>
    </div>
  );
}

/**
 * LaunchFunnelKPIs - Row of KPI cards for launch funnel metrics
 */
interface LaunchFunnelKPIsProps {
  awarenessTarget: number;
  awarenessFinal: number;
  trialTarget: number;
  trialFinal: number;
  repeatTarget: number;
  repeatFinal: number;
  volumeFinal: number;
  volumeUnit?: string;
  language?: 'en' | 'es';
  className?: string;
}

export function LaunchFunnelKPIs({
  awarenessTarget,
  awarenessFinal,
  trialTarget,
  trialFinal,
  repeatTarget,
  repeatFinal,
  volumeFinal,
  volumeUnit = 'K',
  language = 'en',
  className = '',
}: LaunchFunnelKPIsProps) {
  const t = language === 'es';

  const formatVolume = (value: number) => {
    if (volumeUnit === 'M' || value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    return `${(value / 1000).toFixed(1)}K`;
  };

  const meetsTarget = (actual: number, target: number) => actual >= target;

  const kpis = [
    {
      label: t ? 'Awareness Final' : 'Final Awareness',
      value: `${awarenessFinal.toFixed(0)}%`,
      target: `${awarenessTarget}%`,
      meetsTarget: meetsTarget(awarenessFinal, awarenessTarget),
      color: COLORS.awareness,
    },
    {
      label: t ? 'Trial Final' : 'Final Trial',
      value: `${trialFinal.toFixed(0)}%`,
      target: `${trialTarget}%`,
      meetsTarget: meetsTarget(trialFinal, trialTarget),
      color: COLORS.trial,
    },
    {
      label: t ? 'Repeat Final' : 'Final Repeat',
      value: `${repeatFinal.toFixed(0)}%`,
      target: `${repeatTarget}%`,
      meetsTarget: meetsTarget(repeatFinal, repeatTarget),
      color: COLORS.repeat,
    },
    {
      label: t ? 'Volumen S12' : 'Volume S12',
      value: formatVolume(volumeFinal),
      target: null,
      meetsTarget: true,
      color: COLORS.volume,
    },
  ];

  return (
    <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 ${className}`}>
      {kpis.map((kpi) => (
        <div
          key={kpi.label}
          className="card p-4 text-center"
        >
          <div className="text-2xl font-bold" style={{ color: kpi.color }}>
            {kpi.value}
          </div>
          <div className="text-sm text-white mt-1">{kpi.label}</div>
          {kpi.target && (
            <div className={`text-xs mt-2 ${kpi.meetsTarget ? 'text-success-400' : 'text-warning-400'}`}>
              {kpi.meetsTarget ? '✓' : '!'} Target: {kpi.target}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export { COLORS as TRAJECTORY_COLORS };
